import { defineStore } from 'pinia'
import { ref } from 'vue'
import { supabase } from '@/lib/supabase'
import type { Resource, ResourceCategoryInfo, ResourceTypeInfo } from '@/types/education.types'
import { RESOURCE_CATEGORIES, RESOURCE_TYPES, SAMPLE_RESOURCES } from '@/types/education.types'
import type { Opportunity, OpportunityTypeInfo } from '@/types/earn.types'
import { OPPORTUNITY_TYPES, SAMPLE_OPPORTUNITIES } from '@/types/earn.types'
import { config } from '@/config'
import { useUiStore } from '@/stores/ui.store'
import { useAuthStore } from '@/stores/auth.store'
import { cmApi } from '@/lib/api'


import * as llmService from '@/services/llm.service'

export interface AdminStats {
    totalMembers: number
    unlinkedProfiles: number
    activeToday: number
    upcomingEvents: number
    activeChapters: number
    activeCities: number
    activeCountries: number
    totalChatSessions: number
    lastSync: string | null
}

export interface LumaSyncSettings {
    environment?: 'development' | 'production'
    current_app_env?: 'development' | 'production'
    is_running?: boolean
    enabled: boolean
    interval_hours: number
    run_at_utc: string
    run_at_enabled: boolean
    notify_emails: string[]
    main_calendar_id: string
    min_delay_ms: number
    max_delay_ms: number
    last_run: string | null
    last_result: any | null
}

export const useAdminStore = defineStore('admin', () => {
    // State
    const resourceCategories = ref<ResourceCategoryInfo[]>([])
    const resourceTypes = ref<ResourceTypeInfo[]>([])
    const resources = ref<Resource[]>([])
    const opportunityTypes = ref<OpportunityTypeInfo[]>([])
    const opportunities = ref<Opportunity[]>([])
    const availableProviders = ref<any[]>([])
    const availableModels = ref<string[]>([])

    // Luma Calendar Sync State
    const lumaSyncSettings = ref<LumaSyncSettings>({
        environment: 'development',
        enabled: false,
        interval_hours: 24,
        run_at_utc: '14:00',
        run_at_enabled: true,
        notify_emails: ['events@cryptomondays.io'],
        main_calendar_id: 'cryptomondaysofficial',
        min_delay_ms: 1500,
        max_delay_ms: 3500,
        last_run: null,
        last_result: null
    })
    const lumaSyncLoading = ref(false)
    const lumaSyncRunning = ref(false)
    const chapterLumaAccess = ref<Record<string, boolean>>({})

    // Matchmaking & Global Settings
    const settings = ref({
        enableImportedMatchmaking: false,
        enableImportedInvites: false,
        passwordResetRedirectUrl: '',
        chapterRequestAlertEmails: '',
        partnerSponsorAlertEmails: '',
        autoApproveOpportunities: false,
        autoApproveCommunityUpdates: false,
        autoApproveEducationalResources: false,
        dailyTokenLimit: 5000,
        communityLlmProvider: '',
        communityLlmModel: '',
        eventAdminEmails: 'eventadmins@cryptomondays.io',
        archivePlaylists: 'Daily Calls Videos',
        importMaxPerBatch: 20,
        importMaxBatchesPerDay: 1,
        importMaxReinviteCount: 2,
        importInviteTtlDays: 60,
        importDeleteGraceDays: 30,
        importUninvitedTtlDays: 90,
        easyNavDefaultEnabled: true,   // EasyNav guided onboarding on by default
        sponsorWarmInviteEmails: '',
    })

    const stats = ref<AdminStats>({
        totalMembers: 0,
        unlinkedProfiles: 0,
        activeToday: 0,
        upcomingEvents: 0,
        activeChapters: 0,
        activeCities: 0,
        activeCountries: 0,
        totalChatSessions: 0,
        lastSync: null,
    })

    const isLoading = ref(false)
    const error = ref<string | null>(null)
    const settingsLoaded = ref(false)

    // Staleness tracking
    const _lastLearnFetch = ref(0)
    const _lastEarnFetch = ref(0)
    const _lastStatsFetch = ref(0)

    function _staleMs() { return config.session.dataStaleMinutes * 60 * 1000 }

    function needsLearnRefresh(): boolean {
        const uiStore = useUiStore()
        return resources.value.length === 0 ||
            Date.now() - _lastLearnFetch.value > _staleMs() ||
            _lastLearnFetch.value < uiStore.dataInvalidatedAt
    }

    function needsEarnRefresh(): boolean {
        const uiStore = useUiStore()
        return opportunities.value.length === 0 ||
            Date.now() - _lastEarnFetch.value > _staleMs() ||
            _lastEarnFetch.value < uiStore.dataInvalidatedAt
    }

    function needsStatsRefresh(): boolean {
        const uiStore = useUiStore()
        return Date.now() - _lastStatsFetch.value > _staleMs() ||
            _lastStatsFetch.value < uiStore.dataInvalidatedAt
    }

    // Actions
    async function loadStats(): Promise<void> {
        isLoading.value = true
        error.value = null
        try {
            const { count: totalCount, error: memberErr } = await supabase
                .from('profiles')
                .select('*', { count: 'exact', head: true })
                .neq('status', 'pending')

            if (memberErr) throw memberErr
            stats.value.totalMembers = totalCount || 0

            const { count: upcomingCount, error: eventErr } = await supabase
                .from('events')
                .select('*', { count: 'exact', head: true })
                .eq('status', 'upcoming')

            if (eventErr) throw eventErr
            stats.value.upcomingEvents = upcomingCount || 0

            const { count: unlinkedCount, error: unlinkedErr } = await supabase
                .from('profiles')
                .select('*', { count: 'exact', head: true })
                .eq('status', 'pending')

            if (unlinkedErr) throw unlinkedErr
            stats.value.unlinkedProfiles = unlinkedCount || 0

            const { data: chatData } = await cmApi.get('/stats/chats')
            stats.value.totalChatSessions = chatData?.count || 0

            const { data: chaptersData, error: chapterErr } = await supabase
                .from('chapters')
                .select('country, city')
                .eq('status', 'active')

            if (chapterErr) throw chapterErr
            stats.value.activeChapters = chaptersData?.length || 0
            stats.value.activeCountries = new Set(chaptersData?.map(c => c.country).filter(Boolean)).size
            stats.value.activeCities = new Set(chaptersData?.map(c => c.city).filter(Boolean)).size

            await loadSettings()
            _lastStatsFetch.value = Date.now()
        } catch (e: any) {
            console.error('Failed to load stats:', e)
            error.value = e.message || 'Failed to load stats'
        } finally {
            isLoading.value = false
        }
    }

    async function loadSettings(): Promise<void> {
        try {
            const { data, error: dbError } = await supabase
                .from('global_settings')
                .select('*')
                .eq('server_name', config.serverName)

            if (dbError) throw dbError

            if (data) {
                const newSettings: any = {}
                data.forEach(s => {
                    if (s.key === 'matchmaking_imported') newSettings.enableImportedMatchmaking = s.value === 'true'
                    if (s.key === 'invites_enabled') newSettings.enableImportedInvites = s.value === 'true'
                    if (s.key === 'pw_reset_url') newSettings.passwordResetRedirectUrl = s.value
                    if (s.key === 'chapter_request_alert_emails') newSettings.chapterRequestAlertEmails = s.value || ''
                    if (s.key === 'partner_sponsor_alert_emails') newSettings.partnerSponsorAlertEmails = s.value || ''
                    if (s.key === 'auto_approve_opportunities') newSettings.autoApproveOpportunities = s.value === 'true'
                    if (s.key === 'auto_approve_community_updates') newSettings.autoApproveCommunityUpdates = s.value === 'true'
                    if (s.key === 'auto_approve_educational_resources') newSettings.autoApproveEducationalResources = s.value === 'true'
                    if (s.key === 'daily_token_limit') newSettings.dailyTokenLimit = parseInt(s.value) || 5000
                    if (s.key === 'community_llm_provider') newSettings.communityLlmProvider = s.value || ''
                    if (s.key === 'community_llm_model') newSettings.communityLlmModel = s.value || ''
                    if (s.key === 'event_admin_emails') newSettings.eventAdminEmails = s.value || 'eventadmins@cryptomondays.io'
                    if (s.key === 'archive_playlists') newSettings.archivePlaylists = s.value || 'Daily Calls Videos'
                    if (s.key === 'import_max_per_batch') newSettings.importMaxPerBatch = parseInt(s.value) || 20
                    if (s.key === 'import_max_batches_per_day') newSettings.importMaxBatchesPerDay = parseInt(s.value) || 1
                    if (s.key === 'import_max_reinvite_count') newSettings.importMaxReinviteCount = parseInt(s.value) || 2
                    if (s.key === 'import_invite_ttl_days') newSettings.importInviteTtlDays = parseInt(s.value) || 60
                    if (s.key === 'import_delete_grace_days') newSettings.importDeleteGraceDays = parseInt(s.value) || 30
                    if (s.key === 'import_uninvited_ttl_days') newSettings.importUninvitedTtlDays = parseInt(s.value) || 90
                    if (s.key === 'easynav_default_enabled') newSettings.easyNavDefaultEnabled = s.value !== 'false'
                    if (s.key === 'sponsor_warm_invite_emails') newSettings.sponsorWarmInviteEmails = s.value || ''
                })
                settings.value = { ...settings.value, ...newSettings }
                settingsLoaded.value = true
            }
        } catch (e) {
            console.error('Failed to load settings:', e)
        }
    }

    async function updateSettings(newSettings: Partial<typeof settings.value>) {
        const authStore = useAuthStore()
        const currentUserId = authStore.user?.id || null

        settings.value = { ...settings.value, ...newSettings }
        try {
            const promises = []
            if (newSettings.enableImportedMatchmaking !== undefined) {
                promises.push(supabase.from('global_settings').upsert({
                    key: 'matchmaking_imported',
                    value: String(newSettings.enableImportedMatchmaking),
                    server_name: config.serverName,
                    updated_by: currentUserId
                }))
            }
            if (newSettings.enableImportedInvites !== undefined) {
                promises.push(supabase.from('global_settings').upsert({
                    key: 'invites_enabled',
                    value: String(newSettings.enableImportedInvites),
                    server_name: config.serverName,
                    updated_by: currentUserId
                }))
            }
            if (newSettings.passwordResetRedirectUrl !== undefined) {
                promises.push(supabase.from('global_settings').upsert({
                    key: 'pw_reset_url',
                    value: newSettings.passwordResetRedirectUrl,
                    server_name: config.serverName,
                    updated_by: currentUserId
                }))
            }
            if (newSettings.chapterRequestAlertEmails !== undefined) {
                promises.push(supabase.from('global_settings').upsert({
                    key: 'chapter_request_alert_emails',
                    value: newSettings.chapterRequestAlertEmails,
                    server_name: config.serverName,
                    updated_by: currentUserId
                }))
            }
            if (newSettings.partnerSponsorAlertEmails !== undefined) {
                promises.push(supabase.from('global_settings').upsert({
                    key: 'partner_sponsor_alert_emails',
                    value: newSettings.partnerSponsorAlertEmails,
                    server_name: config.serverName,
                    updated_by: currentUserId
                }))
            }
            if (newSettings.autoApproveOpportunities !== undefined) {
                promises.push(supabase.from('global_settings').upsert({
                    key: 'auto_approve_opportunities',
                    value: String(newSettings.autoApproveOpportunities),
                    server_name: config.serverName,
                    updated_by: currentUserId
                }))
            }
            if (newSettings.autoApproveCommunityUpdates !== undefined) {
                promises.push(supabase.from('global_settings').upsert({
                    key: 'auto_approve_community_updates',
                    value: String(newSettings.autoApproveCommunityUpdates),
                    server_name: config.serverName,
                    updated_by: currentUserId
                }))
            }
            if (newSettings.autoApproveEducationalResources !== undefined) {
                promises.push(supabase.from('global_settings').upsert({
                    key: 'auto_approve_educational_resources',
                    value: String(newSettings.autoApproveEducationalResources),
                    server_name: config.serverName,
                    updated_by: currentUserId
                }))
            }
            if (newSettings.dailyTokenLimit !== undefined) {
                promises.push(supabase.from('global_settings').upsert({
                    key: 'daily_token_limit',
                    value: String(newSettings.dailyTokenLimit),
                    server_name: config.serverName,
                    updated_by: currentUserId
                }))
            }
            if (newSettings.communityLlmProvider !== undefined) {
                promises.push(supabase.from('global_settings').upsert({
                    key: 'community_llm_provider',
                    value: newSettings.communityLlmProvider,
                    server_name: config.serverName,
                    updated_by: currentUserId
                }))
            }
            if (newSettings.communityLlmModel !== undefined) {
                promises.push(supabase.from('global_settings').upsert({
                    key: 'community_llm_model',
                    value: newSettings.communityLlmModel,
                    server_name: config.serverName,
                    updated_by: currentUserId
                }))
            }
            if (newSettings.eventAdminEmails !== undefined) {
                promises.push(supabase.from('global_settings').upsert({
                    key: 'event_admin_emails',
                    value: newSettings.eventAdminEmails,
                    server_name: config.serverName,
                    updated_by: currentUserId
                }))
            }
            if (newSettings.archivePlaylists !== undefined) {
                promises.push(supabase.from('global_settings').upsert({
                    key: 'archive_playlists',
                    value: newSettings.archivePlaylists,
                    server_name: config.serverName,
                    updated_by: currentUserId
                }))
            }
            if (newSettings.importMaxPerBatch !== undefined) {
                promises.push(supabase.from('global_settings').upsert({
                    key: 'import_max_per_batch',
                    value: String(newSettings.importMaxPerBatch),
                    server_name: config.serverName,
                    updated_by: currentUserId
                }))
            }
            if (newSettings.importMaxBatchesPerDay !== undefined) {
                promises.push(supabase.from('global_settings').upsert({
                    key: 'import_max_batches_per_day',
                    value: String(newSettings.importMaxBatchesPerDay),
                    server_name: config.serverName,
                    updated_by: currentUserId
                }))
            }
            if (newSettings.importMaxReinviteCount !== undefined) {
                promises.push(supabase.from('global_settings').upsert({
                    key: 'import_max_reinvite_count',
                    value: String(newSettings.importMaxReinviteCount),
                    server_name: config.serverName,
                    updated_by: currentUserId
                }))
            }
            if (newSettings.importInviteTtlDays !== undefined) {
                promises.push(supabase.from('global_settings').upsert({
                    key: 'import_invite_ttl_days',
                    value: String(newSettings.importInviteTtlDays),
                    server_name: config.serverName,
                    updated_by: currentUserId
                }))
            }
            if (newSettings.importDeleteGraceDays !== undefined) {
                promises.push(supabase.from('global_settings').upsert({
                    key: 'import_delete_grace_days',
                    value: String(newSettings.importDeleteGraceDays),
                    server_name: config.serverName,
                    updated_by: currentUserId
                }))
            }
            if (newSettings.importUninvitedTtlDays !== undefined) {
                promises.push(supabase.from('global_settings').upsert({
                    key: 'import_uninvited_ttl_days',
                    value: String(newSettings.importUninvitedTtlDays),
                    server_name: config.serverName,
                    updated_by: currentUserId
                }))
            }
            if (newSettings.easyNavDefaultEnabled !== undefined) {
                promises.push(supabase.from('global_settings').upsert({
                    key: 'easynav_default_enabled',
                    value: String(newSettings.easyNavDefaultEnabled),
                    server_name: config.serverName,
                    updated_by: currentUserId
                }))
            }
            if (newSettings.sponsorWarmInviteEmails !== undefined) {
                promises.push(supabase.from('global_settings').upsert({
                    key: 'sponsor_warm_invite_emails',
                    value: newSettings.sponsorWarmInviteEmails,
                    server_name: config.serverName,
                    updated_by: currentUserId
                }))
            }
            await Promise.all(promises)
        } catch (e) {
            console.error('Failed to persist settings:', e)
        }
    }

    async function triggerSync(): Promise<boolean> {
        stats.value.lastSync = new Date().toISOString()
        return true
    }

    function $reset(): void {
        stats.value = {
            totalMembers: 0,
            unlinkedProfiles: 0,
            activeToday: 0,
            upcomingEvents: 0,
            activeChapters: 0,
            activeCities: 0,
            activeCountries: 0,
            totalChatSessions: 0,
            lastSync: null,
        }
        isLoading.value = false
        error.value = null
    }

    // AI Actions
    async function fetchAvailableProviders() {
        const providers = await llmService.fetchCommunityProviders()
        // Normalize: Ensure every provider has a valid 'id'
        const normalizedProviders = providers.map(p => ({
            ...p,
            id: String(p.id || (p as any).provider || (p as any).key || p.name?.toLowerCase() || '')
        }))
        console.log('[AdminStore] Normalized Providers:', normalizedProviders.map(p => ({ id: p.id, name: p.name })))
        availableProviders.value = normalizedProviders
        return normalizedProviders
    }

    async function fetchAvailableModels(provider: string) {
        const models = await llmService.fetchProviderModels(provider)
        availableModels.value = models
        return models
    }

    async function testLlmConfiguration(provider: string, model: string) {
        isLoading.value = true
        try {
            return await llmService.testCommunityLlm(provider, model)
        } finally {
            isLoading.value = false
        }
    }

    // Education & Earn Actions
    async function loadLearnData(allStatuses = false) {
        isLoading.value = true
        try {
            const [cats, types, res] = await Promise.all([
                supabase.from('resource_categories').select('*').order('sort_order'),
                supabase.from('resource_types').select('*').order('label'),
                allStatuses
                    ? supabase.from('resources').select('*, submitter:profiles!resources_submitted_by_fkey(name)').order('created_at', { ascending: false })
                    : supabase.from('resources').select('*').eq('status', 'approved').order('created_at', { ascending: false })
            ])
            if (cats.data) resourceCategories.value = cats.data
            if (types.data) resourceTypes.value = types.data
            if (res.data) {
                resources.value = res.data.map((r: any) => ({
                    ...r,
                    submitted_by_name: r.submitter?.name || null,
                    submitter: undefined,
                }))
            }
            _lastLearnFetch.value = Date.now()
        } finally {
            isLoading.value = false
        }
    }

    async function createResourceCategory(category: Partial<ResourceCategoryInfo>) {
        isLoading.value = true
        error.value = null
        try {
            const { error: err } = await supabase.from('resource_categories').insert(category)
            if (err) throw err
            await loadLearnData()
            return true
        } catch (err: any) {
            error.value = err.message
            return false
        } finally {
            isLoading.value = false
        }
    }

    async function updateResourceCategory(id: string, changes: Partial<ResourceCategoryInfo>) {
        isLoading.value = true
        error.value = null
        try {
            const { id: _, ...payload } = changes as any
            const { error: err } = await supabase.from('resource_categories').update(payload).eq('id', id)
            if (err) throw err
            await loadLearnData()
            return true
        } catch (err: any) {
            error.value = err.message
            return false
        } finally {
            isLoading.value = false
        }
    }

    async function deleteResourceCategory(id: string) {
        isLoading.value = true
        error.value = null
        try {
            const { error: err } = await supabase.from('resource_categories').delete().eq('id', id)
            if (err) throw err
            await loadLearnData()
            return true
        } catch (err: any) {
            error.value = err.message
            return false
        } finally {
            isLoading.value = false
        }
    }

    async function createResourceType(type: Partial<ResourceTypeInfo>) {
        isLoading.value = true
        error.value = null
        try {
            const { error: err } = await supabase.from('resource_types').insert(type)
            if (err) throw err
            await loadLearnData()
            return true
        } catch (err: any) {
            error.value = err.message
            return false
        } finally {
            isLoading.value = false
        }
    }

    async function updateResourceType(id: string, changes: Partial<ResourceTypeInfo>) {
        isLoading.value = true
        error.value = null
        try {
            const { id: _, ...payload } = changes as any
            const { error: err } = await supabase.from('resource_types').update(payload).eq('id', id)
            if (err) throw err
            await loadLearnData()
            return true
        } catch (err: any) {
            error.value = err.message
            return false
        } finally {
            isLoading.value = false
        }
    }

    async function deleteResourceType(id: string) {
        isLoading.value = true
        error.value = null
        try {
            const { error: err } = await supabase.from('resource_types').delete().eq('id', id)
            if (err) throw err
            await loadLearnData()
            return true
        } catch (err: any) {
            error.value = err.message
            return false
        } finally {
            isLoading.value = false
        }
    }

    async function createResource(resource: Partial<Resource>) {
        isLoading.value = true
        try {
            const { submitted_by_name, submitter, ...payload } = resource as any
            const { error: err } = await supabase.from('resources').insert(payload)
            if (err) throw err
            await loadLearnData(true)
            return true
        } catch (err: any) {
            error.value = err.message
            return false
        } finally {
            isLoading.value = false
        }
    }

    async function submitResource(resource: Partial<Resource>, submitterId: string) {
        isLoading.value = true
        try {
            const autoApprove = settings.value.autoApproveEducationalResources
            const { submitted_by_name, submitter, ...payload } = resource as any
            const { error: err } = await supabase.from('resources').insert({
                ...payload,
                status: autoApprove ? 'approved' : 'pending',
                submitted_by: submitterId,
            })
            if (err) throw err
            await loadLearnData()
            return { success: true, autoApproved: autoApprove }
        } catch (err: any) {
            error.value = err.message
            return { success: false, autoApproved: false }
        } finally {
            isLoading.value = false
        }
    }

    async function updateResource(id: string, changes: Partial<Resource>) {
        isLoading.value = true
        try {
            const { id: _, submitted_by_name, submitter, ...payload } = changes as any
            const { error: err } = await supabase.from('resources').update(payload).eq('id', id)
            if (err) throw err
            await loadLearnData()
            return true
        } catch (err: any) {
            error.value = err.message
            return false
        } finally {
            isLoading.value = false
        }
    }

    async function deleteResource(id: string) {
        isLoading.value = true
        try {
            const { error: err } = await supabase.from('resources').delete().eq('id', id)
            if (err) throw err
            await loadLearnData()
            return true
        } catch (err: any) {
            error.value = err.message
            return false
        } finally {
            isLoading.value = false
        }
    }

    async function loadEarnData(allStatuses = false) {
        isLoading.value = true
        try {
            const [typesRes, oppsRes] = await Promise.all([
                cmApi.get('/opportunities/types'),
                cmApi.get('/opportunities', { params: { allStatuses } })
            ])
            if (typesRes.data) opportunityTypes.value = typesRes.data
            if (oppsRes.data) opportunities.value = oppsRes.data
            
            _lastEarnFetch.value = Date.now()
        } finally {
            isLoading.value = false
        }
    }

    async function createOpportunity(opp: Partial<Opportunity>) {
        isLoading.value = true
        try {
            const { submitted_by_name, submitter, ...payload } = opp as any
            await cmApi.post('/opportunities', payload)
            await loadEarnData(true)
            return true
        } catch (err: any) {
            error.value = err.message
            return false
        } finally {
            isLoading.value = false
        }
    }

    async function submitOpportunity(opp: Partial<Opportunity>, _submitterId: string) {
        isLoading.value = true
        try {
            const { submitted_by_name, submitter, ...payload } = opp as any
            const { data } = await cmApi.post('/opportunities', payload)
            await loadEarnData()
            return { success: true, autoApproved: data.autoApproved }
        } catch (err: any) {
            error.value = err.message
            return { success: false, autoApproved: false }
        } finally {
            isLoading.value = false
        }
    }

    async function updateOpportunity(id: string, changes: Partial<Opportunity>) {
        isLoading.value = true
        try {
            const { id: _, submitted_by_name, submitter, ...payload } = changes as any
            await cmApi.patch(`/opportunities/${id}`, payload)
            await loadEarnData()
            return true
        } catch (err: any) {
            error.value = err.message
            return false
        } finally {
            isLoading.value = false
        }
    }

    async function deleteOpportunity(id: string) {
        isLoading.value = true
        try {
            await cmApi.delete(`/opportunities/${id}`)
            await loadEarnData()
            return true
        } catch (err: any) {
            error.value = err.message
            return false
        } finally {
            isLoading.value = false
        }
    }

    async function createOpportunityType(type: Partial<OpportunityTypeInfo>) {
        isLoading.value = true
        error.value = null
        try {
            await cmApi.post('/opportunities/types', type)
            await loadEarnData()
            return true
        } catch (err: any) {
            error.value = err.message
            return false
        } finally {
            isLoading.value = false
        }
    }

    async function updateOpportunityType(id: string, changes: Partial<OpportunityTypeInfo>) {
        isLoading.value = true
        error.value = null
        try {
            const { id: _, ...payload } = changes as any
            await cmApi.patch(`/opportunities/types/${id}`, payload)
            await loadEarnData()
            return true
        } catch (err: any) {
            error.value = err.message
            return false
        } finally {
            isLoading.value = false
        }
    }

    async function deleteOpportunityType(id: string) {
        isLoading.value = true
        error.value = null
        try {
            await cmApi.delete(`/opportunities/types/${id}`)
            await loadEarnData()
            return true
        } catch (err: any) {
            error.value = err.message
            return false
        } finally {
            isLoading.value = false
        }
    }


    async function seedCommunityDefaults() {
        isLoading.value = true
        error.value = null
        try {
            const cats = Object.entries(RESOURCE_CATEGORIES).map(([id, label], idx) => ({ id, label, sort_order: idx }))
            await supabase.from('resource_categories').upsert(cats)
            const types = Object.entries(RESOURCE_TYPES).map(([id, data]) => ({ id, label: data.label, icon: data.icon }))
            await supabase.from('resource_types').upsert(types)
            const res = SAMPLE_RESOURCES.map(r => ({ title: r.title, description: r.description, type_id: r.type_id, category_id: r.category_id, url: r.url, author: r.author, duration: r.duration, featured: r.featured }))
            await supabase.from('resources').upsert(res)
            const oppTypes = Object.entries(OPPORTUNITY_TYPES).map(([id, data], idx) => ({ id, label: data.label, icon: data.icon, color: data.color, sort_order: idx }))
            await supabase.from('opportunity_types').upsert(oppTypes)
            const opps = SAMPLE_OPPORTUNITIES.map(o => ({ title: o.title, description: o.description, type_id: o.type_id, company: o.company, location: o.location, location_type: o.location_type, compensation: o.compensation, url: o.url, tags: o.tags, featured: o.featured, deadline: o.deadline, priority: 0 }))
            await supabase.from('opportunities').upsert(opps)
            await Promise.all([loadLearnData(), loadEarnData()])
            return true
        } catch (err: any) {
            error.value = err.message
            throw err
        } finally {
            isLoading.value = false
        }
    }

    // ── Luma Calendar Sync Actions ──────────────────────────────────────────
    async function loadLumaSyncSettings(env?: string): Promise<LumaSyncSettings | null> {
        lumaSyncLoading.value = true
        try {
            const url = env ? `/luma/sync/settings?env=${encodeURIComponent(env)}` : '/luma/sync/settings'
            const res = await cmApi.get<{ success: boolean; data: LumaSyncSettings }>(url)
            if (res.data?.success && res.data.data) {
                lumaSyncSettings.value = res.data.data
                if (res.data.data.is_running !== undefined) {
                    lumaSyncRunning.value = !!res.data.data.is_running
                }
                return res.data.data
            }
            return null
        } catch (err: any) {
            console.error('Failed to load Luma sync settings:', err)
            return null
        } finally {
            lumaSyncLoading.value = false
        }
    }

    async function saveLumaSyncSettings(updates: Partial<LumaSyncSettings>, env?: string): Promise<boolean> {
        lumaSyncLoading.value = true
        try {
            const url = env ? `/luma/sync/settings?env=${encodeURIComponent(env)}` : '/luma/sync/settings'
            const res = await cmApi.post<{ success: boolean; data: LumaSyncSettings }>(url, { ...updates, environment: env || updates.environment })
            if (res.data?.success && res.data.data) {
                lumaSyncSettings.value = res.data.data
                return true
            }
            return false
        } catch (err: any) {
            console.error('Failed to save Luma sync settings:', err)
            throw err
        } finally {
            lumaSyncLoading.value = false
        }
    }

    async function previewLumaSync(env?: string): Promise<any> {
        lumaSyncRunning.value = true
        try {
            const targetEnv = env || lumaSyncSettings.value.environment || 'development'
            const url = `/luma/sync/preview?env=${encodeURIComponent(targetEnv)}&async=true`
            await cmApi.get<{ success: boolean; data?: any }>(url)

            // Poll settings until background sweep completes
            const maxWaitMs = 600000 // 10 minutes max
            const pollIntervalMs = 3500
            const startTime = Date.now()

            while (Date.now() - startTime < maxWaitMs) {
                await new Promise(r => setTimeout(r, pollIntervalMs))
                const refreshed = await loadLumaSyncSettings(targetEnv)
                if (refreshed && !refreshed.is_running) {
                    break
                }
            }
            return lumaSyncSettings.value.last_result || {}
        } finally {
            lumaSyncRunning.value = false
        }
    }

    async function runLumaSyncNow(env?: string): Promise<any> {
        lumaSyncRunning.value = true
        try {
            const targetEnv = env || lumaSyncSettings.value.environment || 'development'
            const url = `/luma/sync/run?env=${encodeURIComponent(targetEnv)}`
            await cmApi.post<{ success: boolean; data?: any }>(url, { environment: targetEnv, async: true })

            // Poll settings until background sweep completes
            const maxWaitMs = 600000 // 10 minutes max
            const pollIntervalMs = 3500
            const startTime = Date.now()

            while (Date.now() - startTime < maxWaitMs) {
                await new Promise(r => setTimeout(r, pollIntervalMs))
                const refreshed = await loadLumaSyncSettings(targetEnv)
                if (refreshed && !refreshed.is_running) {
                    break
                }
            }
            return lumaSyncSettings.value.last_result || {}
        } finally {
            lumaSyncRunning.value = false
        }
    }

    async function toggleLumaEventComplete(eventKey: string, completed: boolean, env?: string): Promise<any> {
        try {
            const url = env ? `/luma/sync/toggle-complete?env=${encodeURIComponent(env)}` : '/luma/sync/toggle-complete'
            const res = await cmApi.post<{ success: boolean; data: any }>(url, { event_key: eventKey, completed, environment: env })
            if (res.data?.success && res.data.data) {
                // Update local state in last_result
                if (lumaSyncSettings.value.last_result) {
                    const updateItem = (item: any) => {
                        if (item.event_id === eventKey || item.slug === eventKey || item.event_url === eventKey) {
                            return { ...item, is_completed: completed, manually_completed: completed }
                        }
                        return item
                    }
                    if (Array.isArray(lumaSyncSettings.value.last_result.past_unimported)) {
                        lumaSyncSettings.value.last_result.past_unimported = lumaSyncSettings.value.last_result.past_unimported.map(updateItem)
                    }
                    if (Array.isArray(lumaSyncSettings.value.last_result.upcoming_unimported)) {
                        lumaSyncSettings.value.last_result.upcoming_unimported = lumaSyncSettings.value.last_result.upcoming_unimported.map(updateItem)
                    }
                    if (Array.isArray(lumaSyncSettings.value.last_result.completed_events)) {
                        lumaSyncSettings.value.last_result.completed_events = lumaSyncSettings.value.last_result.completed_events.map(updateItem)
                    }
                    if (Array.isArray(lumaSyncSettings.value.last_result.all_tracked_events)) {
                        lumaSyncSettings.value.last_result.all_tracked_events = lumaSyncSettings.value.last_result.all_tracked_events.map(updateItem)
                    }
                }
                return res.data.data
            }
            throw new Error('Failed to update event completion status')
        } catch (err: any) {
            console.error('Failed to toggle event completion:', err)
            throw err
        }
    }

    async function loadChapterLumaAccess(): Promise<Record<string, boolean>> {
        try {
            const res = await cmApi.get<{ success: boolean; data: Record<string, boolean> }>('/luma/sync/chapter-access')
            if (res.data?.success && res.data.data) {
                chapterLumaAccess.value = res.data.data
                return res.data.data
            }
            return chapterLumaAccess.value
        } catch (err) {
            console.error('Failed to load chapter luma access:', err)
            return chapterLumaAccess.value
        }
    }

    async function saveChapterLumaAccess(chapterId: string, hasAccess: boolean): Promise<boolean> {
        try {
            const res = await cmApi.post<{ success: boolean; data: Record<string, boolean> }>('/luma/sync/chapter-access', {
                chapter_id: chapterId,
                has_access: hasAccess
            })
            if (res.data?.success && res.data.data) {
                chapterLumaAccess.value = res.data.data
                return true
            }
            chapterLumaAccess.value[chapterId] = hasAccess
            return true
        } catch (err) {
            console.error('Failed to save chapter luma access:', err)
            chapterLumaAccess.value[chapterId] = hasAccess
            return false
        }
    }

    return {
        resourceCategories,
        resourceTypes,
        resources,
        opportunityTypes,
        opportunities,
        availableProviders,
        availableModels,
        lumaSyncSettings,
        lumaSyncLoading,
        lumaSyncRunning,
        chapterLumaAccess,
        settings,
        stats,
        isLoading,
        error,
        settingsLoaded,
        loadStats,
        loadSettings,
        updateSettings,
        loadLumaSyncSettings,
        saveLumaSyncSettings,
        previewLumaSync,
        runLumaSyncNow,
        toggleLumaEventComplete,
        loadChapterLumaAccess,
        saveChapterLumaAccess,
        needsLearnRefresh,
        needsEarnRefresh,
        needsStatsRefresh,
        fetchAvailableProviders,
        fetchAvailableModels,
        testLlmConfiguration,
        triggerSync,
        $reset,
        loadLearnData,
        createResourceCategory,
        updateResourceCategory,
        deleteResourceCategory,
        createResourceType,
        updateResourceType,
        deleteResourceType,
        createResource,
        submitResource,
        updateResource,
        deleteResource,
        loadEarnData,
        createOpportunity,
        submitOpportunity,
        updateOpportunity,
        deleteOpportunity,
        createOpportunityType,
        updateOpportunityType,
        deleteOpportunityType,
        seedCommunityDefaults
    }
})
